import React from 'react';
import { GroupDTO, CreateLabRequest, InstitutionDTO } from '@/models';
import { Modal, ModalFooter } from '@/components/common';

interface LabFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: CreateLabRequest;
  institutions: InstitutionDTO[];
  groups: GroupDTO[];
  isSaving: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onInstitutionChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const LabFormModal: React.FC<LabFormModalProps> = ({
  isOpen,
  mode,
  formData,
  institutions,
  groups,
  isSaving,
  onInputChange,
  onInstitutionChange,
  onSubmit,
  onClose,
}) => {
  const getInstitutionName = (institutionId: string) => {
    const institution = institutions.find((i) => i.id === institutionId);
    return institution?.name || '-';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title={mode === 'create' ? 'Add New Lab' : 'Edit Lab'}
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={onSubmit}
          confirmText={mode === 'create' ? 'Create' : 'Update'}
          isLoading={isSaving}
          disabled={!formData.name.trim() || !formData.institutionId || !formData.groupId}
        />
      }
    >
      <form>
        <div className="row g-3">
          <div className="col-12">
            <label htmlFor="institutionId" className="form-label">
              Institution <span className="text-danger">*</span>
            </label>
            {mode === 'edit' ? (
              <input
                type="text"
                className="form-control"
                value={getInstitutionName(formData.institutionId)}
                disabled
              />
            ) : (
              <select
                className="form-select"
                id="institutionId"
                name="institutionId"
                value={formData.institutionId}
                onChange={onInstitutionChange}
                required
              >
                <option value="">Select an institution...</option>
                {institutions.map((institution) => (
                  <option key={institution.id} value={institution.id}>
                    {institution.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="col-12">
            <label htmlFor="groupId" className="form-label">
              Group <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              id="groupId"
              name="groupId"
              value={formData.groupId || ''}
              onChange={onInputChange}
              disabled={!formData.institutionId}
            >
              <option value="">Select a group...</option>
              {groups
                .filter((g) => g.institutionId === formData.institutionId)
                .map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="col-12">
            <label htmlFor="name" className="form-label">
              Lab Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={formData.name}
              onChange={onInputChange}
              placeholder="e.g., Chemistry Lab A"
              required
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};
